/**
 * Expert-level React hook for template synchronization.
 * Handles template sync with backend microservice.
 */

import { useState, useEffect, useCallback } from 'react';
import { detectTemplatesInFolder } from '../services/autoTemplateSync';
import { TemplateDiscoveryService, TemplateData } from '../services/templateService';

interface SyncResponse {
  status: string;
  message: string;
  synced_count: number;
  total_templates?: number;
  templates: any[];
  errors?: string[];
}

interface UseTemplateSyncReturn {
  isSyncing: boolean;
  syncResult: SyncResponse | null;
  error: string | null;
  syncTemplates: () => Promise<void>;
  lastSyncTime: Date | null;
}

/**
 * Hook for template synchronization
 */
export const useTemplateSync = (): UseTemplateSyncReturn => {
  const [isSyncing, setIsSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState<SyncResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [lastSyncTime, setLastSyncTime] = useState<Date | null>(null);

  /**
   * Sync templates with backend
   */
  const syncTemplates = useCallback(async () => {
    try {
      setIsSyncing(true);
      setError(null);
      
      console.log('Starting template synchronization...');
      
      // Auto-discover and sync templates using the new service
      const templates = await detectTemplatesInFolder();
      
      const result: SyncResponse = {
        status: 'success',
        message: `Successfully detected ${templates.length} templates`,
        synced_count: templates.length,
        templates: templates
      };
      
      setSyncResult(result);
      setLastSyncTime(new Date());
      
      console.log(`Template sync completed: ${result.synced_count} templates`);
      
      if (result.errors && result.errors.length > 0) {
        console.warn('Sync completed with errors:', result.errors);
      }
      
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error';
      setError(errorMessage);
      console.error('Template sync failed:', errorMessage);
    } finally {
      setIsSyncing(false);
    }
  }, []);

  /**
   * Auto-sync on component mount
   */
  useEffect(() => {
    // Only auto-sync if not already synced recently
    const shouldAutoSync = !lastSyncTime || 
      (Date.now() - lastSyncTime.getTime()) > 5 * 60 * 1000; // 5 minutes
    
    if (shouldAutoSync) {
      syncTemplates();
    }
  }, [syncTemplates, lastSyncTime]);

  return {
    isSyncing,
    syncResult,
    error,
    syncTemplates,
    lastSyncTime
  };
};

/**
 * Hook for template management
 */
export const useTemplates = () => {
  const [templates, setTemplates] = useState<TemplateData[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /**
   * Fetch templates from backend
   */
  const fetchTemplates = useCallback(async (_params: any = {}) => {
    try {
      setLoading(true);
      setError(null);
      
      const localTemplates = TemplateDiscoveryService.discoverTemplates();
      setTemplates(localTemplates);
      console.log('⚠️ fetchTemplates: utilisation templates locaux uniquement (API désactivée)');
      
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error';
      setError(errorMessage);
      console.error('Failed to fetch templates:', errorMessage);
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Get template by ID
   */
  const getTemplate = useCallback(async (templateId: string) => {
    try {
      setLoading(true);
      setError(null);
      
      const localTemplates = TemplateDiscoveryService.discoverTemplates();
      const template = localTemplates.find((t: TemplateData) => t.template_id === templateId);
      
      if (!template) {
        throw new Error(`Template ${templateId} not found in local templates`);
      }
      console.log('⚠️ getTemplate: utilisation templates locaux uniquement (API désactivée)');
      return template;
      
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error';
      setError(errorMessage);
      console.error(`Failed to fetch template ${templateId}:`, errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  return {
    templates,
    loading,
    error,
    fetchTemplates,
    getTemplate
  };
};

export default useTemplateSync;




